import { Card, Input, Pagination, Table, Empty, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import axios from '../config/axios';
import dayjs from '../config/dayjs';
import CopyableText from './copyable-text';
import { Spinner } from './spinner';
import { handleAPIError } from '../utils/helpers';
import { SearchOutlined } from '@ant-design/icons';

interface UserContactsProps {
	userId: number;
}

interface UserContact {
	id: number;
	name: string;
	contact: string;
	is_frequently_called: boolean;
	created_at: string;
	user: number;
}

interface ContactsResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: UserContact[];
}

export const UserContacts = ({ userId }: UserContactsProps) => {
	// State for regular contacts
	const [contactsPage, setContactsPage] = useState(1);
	const [contactsLimit, setContactsLimit] = useState(50);
	const [contactsSearch, setContactsSearch] = useState('');

	// State for frequently called contacts
	const [frequentPage, setFrequentPage] = useState(1);
	const [frequentLimit, setFrequentLimit] = useState(50);
	const [frequentSearch, setFrequentSearch] = useState('');

	// Fetch regular contacts
	const {
		data: contactsData,
		isLoading: contactsLoading,
		error: contactsError,
	} = useQuery<ContactsResponse>({
		queryKey: ['user-contacts', userId, contactsPage, contactsLimit, contactsSearch, false],
		queryFn: async () => {
			const response = await axios.get(`/admin/users/${userId}/contacts`, {
				params: {
					frequently_called: false,
					page: contactsPage,
					limit: contactsLimit,
					search_term: contactsSearch || undefined,
				},
				withCredentials: true,
			});
			return response.data;
		},
		enabled: !!userId,
	});

	// Fetch frequently called contacts
	const {
		data: frequentData,
		isLoading: frequentLoading,
		error: frequentError,
	} = useQuery<ContactsResponse>({
		queryKey: ['user-contacts', userId, frequentPage, frequentLimit, frequentSearch, true],
		queryFn: async () => {
			const response = await axios.get(`/admin/users/${userId}/contacts`, {
				params: {
					frequently_called: true,
					page: frequentPage,
					limit: frequentLimit,
					search_term: frequentSearch || undefined,
				},
				withCredentials: true,
			});
			return response.data;
		},
		enabled: !!userId,
	});

	// Handle errors
	if (contactsError) {
		handleAPIError(contactsError);
	}
	if (frequentError) {
		handleAPIError(frequentError);
	}

	// Table columns
	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: '30%',
		},
		{
			title: 'Contact',
			dataIndex: 'contact',
			key: 'contact',
			width: '30%',
			render: (contact: string) => (
				<CopyableText text={contact}>
					<a
						className="text-base"
						href={`tel:+${contact}`}
					>
						{contact}
					</a>
				</CopyableText>
			),
		},
		{
			title: 'Saved On',
			dataIndex: 'created_at',
			key: 'created_at',
			width: '40%',
			render: (date: string) => {
				const d = dayjs(date);
				const daysAgo = dayjs().diff(d, 'day');
				const isRecent = daysAgo <= 7;
				return (
					<div className="flex items-center gap-2">
						<span className="text-sm text-gray-700">{d.format('DD MMM YYYY, HH:mm')}</span>
						{isRecent && <Tag color="blue">New</Tag>}
					</div>
				);
			},
		},
	];

	return (
		<div className="space-y-6">
			{/* Regular Contacts */}
			<Card
				title={
					<div className="flex items-center gap-2">
						<span>Contacts</span>
						{contactsData?.count != null && (
							<Tag color="default">{contactsData.count} total</Tag>
						)}
					</div>
				}
				className="shadow-sm"
			>
				<div className="mb-4">
					<Input
						placeholder="Search contacts by name..."
						prefix={<SearchOutlined />}
						value={contactsSearch}
						onChange={(e) => {
							setContactsSearch(e.target.value);
							setContactsPage(1); // Reset to first page on search
						}}
						allowClear
						size="large"
					/>
				</div>

				{contactsLoading ? (
					<div className="flex h-48 items-center justify-center">
						<Spinner />
					</div>
				) : contactsData?.results && contactsData.results.length > 0 ? (
					<>
						<Table
							dataSource={contactsData.results}
							columns={columns}
							rowKey="id"
							pagination={false}
							size="small"
						/>
						<div className="mt-4 flex justify-end">
							<Pagination
								current={contactsPage}
								total={contactsData.count}
								pageSize={contactsLimit}
								onChange={(page, pageSize) => {
									setContactsPage(page);
									setContactsLimit(pageSize);
								}}
								showSizeChanger
								showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} contacts`}
								pageSizeOptions={['10', '20', '50', '100']}
							/>
						</div>
					</>
				) : (
					<Empty description="No contacts found" />
				)}
			</Card>

			{/* Frequently Called Contacts */}
			<Card
				title={
					<div className="flex items-center gap-2">
						<span>Frequently Called Contacts</span>
						{frequentData?.count != null && (
							<Tag color="default">{frequentData.count} total</Tag>
						)}
					</div>
				}
				className="shadow-sm"
			>
				<div className="mb-4">
					<Input
						placeholder="Search frequently called contacts by name..."
						prefix={<SearchOutlined />}
						value={frequentSearch}
						onChange={(e) => {
							setFrequentSearch(e.target.value);
							setFrequentPage(1); // Reset to first page on search
						}}
						allowClear
						size="large"
					/>
				</div>

				{frequentLoading ? (
					<div className="flex h-48 items-center justify-center">
						<Spinner />
					</div>
				) : frequentData?.results && frequentData.results.length > 0 ? (
					<>
						<Table
							dataSource={frequentData.results}
							columns={columns}
							rowKey="id"
							pagination={false}
							size="small"
						/>
						<div className="mt-4 flex justify-end">
							<Pagination
								current={frequentPage}
								total={frequentData.count}
								pageSize={frequentLimit}
								onChange={(page, pageSize) => {
									setFrequentPage(page);
									setFrequentLimit(pageSize);
								}}
								showSizeChanger
								showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} contacts`}
								pageSizeOptions={['10', '20', '50', '100']}
							/>
						</div>
					</>
				) : (
					<Empty description="No frequently called contacts found" />
				)}
			</Card>
		</div>
	);
};
